import React from 'react';
import {useIntl} from 'react-intl';
import styled from 'styled-components';
import {Icon, tokensForThemeProvider} from '@forecast-it/design-system';
import TimeregActionButton from '../../../../shared/components/time-reg-table/TimeregActionButton';
import {describeEnd, describePattern, describeShortfallFlag, formatSetDate, TaskSetShortfall} from './describeTaskSet';
import {taskSetColour} from './taskSetColours';

const tokens = tokensForThemeProvider.sky.sys;

/**
 * The list's own wrapper. No border, background or scroll cap: the drawer body
 * provides all three, and a second scrolling region nested inside it would give
 * the list its own scrollbar next to the drawer's.
 */
const Wrapper = styled.div`
	font-size: 13px;
	color: rgba(0, 0, 0, 0.87);
`;

const PanelTitle = styled.div`
	font-size: 14px;
	font-weight: 600;
	color: rgba(0, 0, 0, 0.87);
	padding: 12px 12px 2px 12px;
`;

/** Ties the row to the colour its instances' chips carry on the task rows. */
const Swatch = styled.span<{colour: string}>`
	display: inline-block;
	width: 10px;
	height: 10px;
	border-radius: 3px;
	background: ${({colour}) => colour};
	margin-right: 8px;
	flex-shrink: 0;
`;

const NameWithSwatch = styled.span`
	display: inline-flex;
	align-items: center;
`;

const Table = styled.table`
	width: 100%;
	border-collapse: collapse;
`;

const HeaderCell = styled.th`
	text-align: left;
	font-size: 10px;
	font-weight: 600;
	letter-spacing: 0.5px;
	text-transform: uppercase;
	color: #7d7d7d;
	padding: 8px 12px;
	border-bottom: 1px solid #e6e6e6;
	white-space: nowrap;
`;

const NumericHeaderCell = styled(HeaderCell)`
	text-align: right;
`;

const Row = styled.tr<{highlighted: boolean}>`
	height: 30px;
	background: ${({highlighted}) => (highlighted ? '#f6f3fe' : 'transparent')};

	&:not(:last-child) > td {
		border-bottom: 1px solid #f3f3f3;
	}
`;

const Cell = styled.td`
	padding: 6px 12px;
	vertical-align: middle;
	font-size: 12px;
	color: #535353;
`;

/** The row's anchor, so it carries the list's full size and weight. */
const NameCell = styled(Cell)`
	font-size: 13px;
	font-weight: 600;
	color: rgba(0, 0, 0, 0.87);
`;

/** Right-aligned with tabular figures so the counts line up down the column. */
const NumericCell = styled(Cell)`
	text-align: right;
	font-variant-numeric: tabular-nums;
`;

/* The diverged count rides in the Instances cell rather than taking a column of
   its own: the drawer's table is 599px and Set already wraps, so a sixth header
   word would take its width from the one column people read. Dimmed and
   unpadded so the total stays the number the eye lands on, and nowrap so the
   pair never splits across two lines in a 87px column. */
const DivergedNote = styled.span`
	margin-left: 6px;
	color: #a1a1a1;
	white-space: nowrap;
`;

const ActionCell = styled(Cell)`
	text-align: right;
	white-space: nowrap;
`;

/** Holds the row's two icon buttons at a comfortable hit size without widening the row. */
const RowActions = styled.span`
	display: inline-flex;
	align-items: center;
	gap: 10px;
	justify-content: flex-end;
`;

const EmptyState = styled.div`
	padding: 16px 12px;
	color: #7d7d7d;
`;

/** Shown in place of a phrase this version cannot describe, never a guess. */
const Unknown = styled.span`
	color: #a1a1a1;
`;

/**
 * The line under Ends for a set whose generation stopped short of the end it
 * was given. The icon carries the warning colour; the text stays the same
 * neutral secondary colour as the list's other annotations, since the design
 * system has no warning text colour and that contrast is too low to read.
 */
const ShortfallFlag = styled.div`
	display: flex;
	align-items: center;
	gap: 4px;
	margin-top: 2px;
	font-size: 12px;
	color: ${tokens.color.text.content.auxiliary};
`;

export interface TaskSetRow {
	readonly id: string;
	readonly name?: string | null;
	readonly frequency?: string | null;
	readonly intervalLength?: number | null;
	readonly weekdays?: readonly (string | null)[] | null;
	readonly monthlyWeekday?: string | null;
	readonly monthlyMode?: string | null;
	readonly dayOfMonth?: number | null;
	readonly weekOrdinal?: number | null;
	readonly boundType?: string | null;
	readonly boundCount?: number | null;
	readonly boundEndDate?: string | null;
	readonly materialisedTo?: string | null;
	/* The instance the set ends after. Ends names it rather than printing a
	   bare date, matching the edit form's field word for word. */
	readonly lastInstance?: {readonly companyTaskId?: number | null; readonly startDate?: string | null} | null;
	/* Present only when generation stopped before the end this set was given.
	   Flagged under Ends -- it only means something next to the end it qualifies. */
	readonly generationShortfall?: TaskSetShortfall | null;
	readonly seedDate?: string | null;
	readonly instanceCount?: number | null;
	/* How many instances have been hand-edited away from the set. Rendered
	   beside the total, not in its own column: a diverged count only says
	   anything next to the total it is a fraction of. */
	readonly exceptionCount?: number | null;
	readonly parentPhase?: {readonly id: string; readonly name?: string | null} | null;
	/* The stored defaults the edit form opens with. A set created before those
	   columns existed returns null for all of them, and the form renders that as
	   empty rather than borrowing an instance's values. */
	readonly instanceName?: string | null;
	readonly description?: string | null;
	readonly estimateForecast?: number | null;
	readonly billable?: boolean | null;
	readonly role?: {readonly id: string} | null;
	readonly phase?: {readonly id: string} | null;
	readonly defaultLabels?: readonly {readonly id: string}[] | null;
	readonly defaultAssignedPersons?: readonly {readonly id: string}[] | null;
}

export interface TaskSetListPaneProps {
	readonly sets: readonly TaskSetRow[];
	/**
	 * Picked out because the reader arrived from one of its instances. Tinted
	 * rather than scrolled to or selected: the row is already on screen in every
	 * realistic case, and the tint answers "which of these is the one I clicked"
	 * without claiming the row is now in some state.
	 */
	readonly highlightSetId?: string | null;
	/**
	 * Relabels the row's action to View set. The form opens either way -- a
	 * viewer who cannot edit tasks gets it with every control switched off, so
	 * view and edit are one layout -- and the permission only changes what the
	 * action promises.
	 */
	readonly readOnly?: boolean;
	/** Opens the set's form. Drawn for every viewer. */
	onViewSet?: (taskSetId: string) => void;
	/**
	 * Starts the delete for a row. Absent when the viewer cannot delete tasks, and
	 * the icon is then DIMMED rather than removed, so the row's two actions stay in
	 * the same places for every viewer and the control says what it would do.
	 */
	onDeleteSet?: (taskSetId: string) => void;
}

/**
 * The project's task sets, as the list pane of the manage drawer.
 *
 * This exists because a set needs a home that does not depend on one of its
 * instances being on screen. The instances may all be complete, filtered out, or
 * inside a phase the user has collapsed -- and then the chip on an instance, the
 * only other way to reach a set, is unreachable.
 *
 * One row per SET, never a container wrapping the instance rows. Wrapping the rows
 * would reintroduce the collapsible group header by another route, which was
 * rejected: instances are ordinary tasks and have to keep behaving like the rows
 * around them.
 *
 * There is no estimate column. The set now stores its own estimate, but showing
 * it here would compete with the three columns that say when the set runs. The
 * divergence count has no column either -- it rides inside Instances, where it
 * reads as a fraction of the total it sits beside.
 *
 * Takes its rows as a plain prop rather than reading a fragment, so one component
 * serves both pages: scoping feeds it from the page query, the timeline from a
 * query that runs when the drawer opens.
 */
export const TaskSetListPane = ({
	sets,
	highlightSetId = null,
	readOnly = false,
	onViewSet,
	onDeleteSet,
}: TaskSetListPaneProps) => {
	const intl = useIntl();

	if (sets.length === 0) {
		return (
			<Wrapper data-cy="manage-task-sets-panel">
				<EmptyState>{intl.formatMessage({id: 'task_set.manage_empty'})}</EmptyState>
			</Wrapper>
		);
	}

	const unknown = <Unknown>{intl.formatMessage({id: 'task_set.none'})}</Unknown>;

	return (
		<Wrapper data-cy="manage-task-sets-panel">
			<PanelTitle>{intl.formatMessage({id: 'task_set.panel_title'})}</PanelTitle>
			<Table>
				<thead>
					<tr>
						<HeaderCell>{intl.formatMessage({id: 'task_set.set_column'})}</HeaderCell>
						<HeaderCell>{intl.formatMessage({id: 'task_set.pattern'})}</HeaderCell>
						<HeaderCell>{intl.formatMessage({id: 'task_set.first_instance'})}</HeaderCell>
						<HeaderCell>{intl.formatMessage({id: 'task_set.ends'})}</HeaderCell>
						<NumericHeaderCell>{intl.formatMessage({id: 'task_set.instances'})}</NumericHeaderCell>
						<HeaderCell />
					</tr>
				</thead>
				<tbody>
					{sets.map(set => {
						const pattern = describePattern(intl, set);
						/* Ends names the instance the set runs to -- "After T713,
						   14 Dec" -- word for word the sentence the edit form's Ends
						   field shows, from one message key. Two formats for one fact
						   made the list and the form read as different answers. */
						const starts = formatSetDate(intl, set.seedDate);
						const ends = describeEnd(intl, set);
						const shortfall = describeShortfallFlag(intl, set);
						const colour = taskSetColour(set.id);
						return (
							<Row key={set.id} highlighted={set.id === highlightSetId} data-cy="manage-task-sets-row">
								<NameCell>
									<NameWithSwatch>
										<Swatch colour={colour.background} aria-hidden="true" />
										{set.name}
									</NameWithSwatch>
								</NameCell>
								<Cell>{pattern === null ? unknown : pattern}</Cell>
								<Cell>{starts === null ? unknown : starts}</Cell>
								<Cell>
									{ends === null ? unknown : ends}
									{/* Under Ends rather than in a column of its own: it only
									    means something next to the end it qualifies. */}
									{shortfall === null ? null : (
										<ShortfallFlag data-cy="manage-task-sets-shortfall">
											<Icon icon="warning" size="s" color="warning" aria-hidden="true" />
											{shortfall}
										</ShortfallFlag>
									)}
								</Cell>
								<NumericCell>
									{set.instanceCount ?? 0}
									{(set.exceptionCount ?? 0) > 0 ? (
										<DivergedNote>
											{intl.formatMessage({id: 'task_set.diverged_suffix'}, {count: set.exceptionCount})}
										</DivergedNote>
									) : null}
								</NumericCell>
								<ActionCell>
									{/* Icons, not text links, matching the inline row actions the
									    newer design-system surfaces already use. Each carries its
									    own aria-label and a tooltip with the same words, so the
									    action is still named for a screen reader and on hover. */}
									<RowActions>
										<TimeregActionButton
											text={intl.formatMessage({
												id: readOnly ? 'task_set.view_set' : 'task_set.edit_set',
											})}
											icon="edit"
											disabled={!onViewSet}
											onClick={() => onViewSet && onViewSet(set.id)}
											data-cy="task-set-row-edit"
										/>
										<TimeregActionButton
											text={intl.formatMessage({id: 'task_set.delete_set'})}
											icon="trash"
											disabled={!onDeleteSet}
											onClick={() => onDeleteSet && onDeleteSet(set.id)}
											data-cy="task-set-row-delete"
										/>
									</RowActions>
								</ActionCell>
							</Row>
						);
					})}
				</tbody>
			</Table>
		</Wrapper>
	);
};
